"use strict";
import { useQuery } from "@tanstack/react-query";
import {
  VE_META_FRAGMENT,
  fetchFromVeSubgraph,
  isVeMetaHealthy,
  isVeSubgraphAvailable
} from "features/veSubgraph/client";
import { useMemo } from "react";
const EPOCH_SECONDS = 604800;
export function getCurrentEpochStart(nowSeconds = Math.floor(Date.now() / 1e3)) {
  return Math.floor(nowSeconds / EPOCH_SECONDS) * EPOCH_SECONDS;
}
const GAUGES_PAGE_SIZE = 500;
const REWARD_EPOCH_PAGE_SIZE = 1e3;
const GAUGE_FIELDS = `
  id
  kind
  isAlive
  currentWeight
  rewardRate
  periodFinish
  totalStaked
  pool {
    id
    poolType
    tickSpacing
    token0 { id symbol name decimals }
    token1 { id symbol name decimals }
  }
  bribeVotingReward { id }
  feesVotingReward { id }
`;
const REWARD_TOKEN_EPOCH_FIELDS = `
  id
  kind
  amount
  token { id symbol decimals }
  gauge { id }
`;
const GAUGE_DATA_QUERY = `
  query VeGaugeData($epoch: String!) {
    ${VE_META_FRAGMENT}
    gauges(first: ${GAUGES_PAGE_SIZE}, orderBy: id, orderDirection: asc) {
      ${GAUGE_FIELDS}
    }
    rewardTokenEpoches(where: { epoch: $epoch }, first: ${REWARD_EPOCH_PAGE_SIZE}, orderBy: id, orderDirection: asc) {
      ${REWARD_TOKEN_EPOCH_FIELDS}
    }
    epoch(id: $epoch) {
      totalVotes
    }
  }
`;
const GAUGES_PAGE_QUERY = `
  query VeGaugesPage($cursor: Bytes!) {
    gauges(where: { id_gt: $cursor }, first: ${GAUGES_PAGE_SIZE}, orderBy: id, orderDirection: asc) {
      ${GAUGE_FIELDS}
    }
  }
`;
const REWARD_EPOCH_PAGE_QUERY = `
  query VeRewardTokenEpochsPage($epoch: String!, $cursor: ID!) {
    rewardTokenEpoches(
      where: { epoch: $epoch, id_gt: $cursor }
      first: ${REWARD_EPOCH_PAGE_SIZE}
      orderBy: id
      orderDirection: asc
    ) {
      ${REWARD_TOKEN_EPOCH_FIELDS}
    }
  }
`;
export function useVeGaugeData(chainId, enabled = true) {
  const available = isVeSubgraphAvailable(chainId);
  const epochStart = getCurrentEpochStart();
  const query = useQuery({
    queryKey: ["ve-gauge-data", chainId, epochStart],
    queryFn: async () => {
      const data = await fetchFromVeSubgraph(chainId, {
        query: GAUGE_DATA_QUERY,
        variables: { epoch: epochStart.toString() },
        requestId: `veGaugeData:${chainId}`
      });
      const gauges = [...data?.gauges ?? []];
      while (gauges.length > 0 && gauges.length % GAUGES_PAGE_SIZE === 0) {
        const page = await fetchFromVeSubgraph(chainId, {
          query: GAUGES_PAGE_QUERY,
          variables: { cursor: gauges[gauges.length - 1].id },
          requestId: `veGaugeData:gaugesPage:${chainId}`
        });
        if (!page?.gauges?.length) {
          break;
        }
        gauges.push(...page.gauges);
      }
      const rewardTokenEpochs = [...data?.rewardTokenEpoches ?? []];
      while (rewardTokenEpochs.length > 0 && rewardTokenEpochs.length % REWARD_EPOCH_PAGE_SIZE === 0) {
        const page = await fetchFromVeSubgraph(
          chainId,
          {
            query: REWARD_EPOCH_PAGE_QUERY,
            variables: { epoch: epochStart.toString(), cursor: rewardTokenEpochs[rewardTokenEpochs.length - 1].id },
            requestId: `veGaugeData:rewardEpochsPage:${chainId}`
          }
        );
        if (!page?.rewardTokenEpoches?.length) {
          break;
        }
        rewardTokenEpochs.push(...page.rewardTokenEpoches);
      }
      return {
        meta: data?._meta ?? null,
        gauges,
        rewardTokenEpochs,
        totalVotes: data?.epoch?.totalVotes ?? null,
        currentEpoch: epochStart
      };
    },
    enabled: enabled && available,
    staleTime: 3e4,
    refetchInterval: 6e4,
    gcTime: 5 * 6e4,
    retry: 2
  });
  const { dataUpdatedAt } = query;
  const healthy = query.data !== void 0 && isVeMetaHealthy(query.data.meta);
  return useMemo(() => {
    const failed = query.isError || query.data !== void 0 && !healthy;
    const isTrying = enabled && available && !failed;
    return {
      data: healthy ? query.data : void 0,
      isLoading: isTrying && query.isLoading,
      isTrying,
      isActive: isTrying && query.data !== void 0 && healthy
    };
  }, [query.data, query.isError, query.isLoading, enabled, available, healthy, dataUpdatedAt]);
}
export function isV3PoolType(poolType) {
  return poolType === "CL";
}
